import type { IntakeFormData } from "../types";
import { PRIMARY_ENTITY_ID, isAffiliatedListShown } from "../types/structureConstants";

/** Shared per-entity view model used by both the Company Structure and
 *  Ownership Breakdown sections on Review and Thank You. `errorPrefix` is
 *  set only for affiliated entities so field-level validation errors can be
 *  keyed onto their rows. */
export type EntityBlock = {
  id: string;
  entityNumber: number;
  name: string;
  taxId: string;
  employees: number | null;
  covered: boolean | null;
  errorPrefix?: string;
  isPrimary: boolean;
};

/** Entity list for the Plan Structure section: primary + every affiliated
 *  entity (covered and uncovered). Caller gates rendering on
 *  `isAffiliatedListShown(data)` — solo-primary skips this section entirely. */
export function buildEntityBlocks(data: IntakeFormData): EntityBlock[] {
  const blocks: EntityBlock[] = [{
    id: PRIMARY_ENTITY_ID,
    entityNumber: 1,
    name: data.companyLegalName || "Primary group",
    taxId: data.federalTaxId,
    employees: data.primaryEligibleEmployees,
    covered: true,
    isPrimary: true,
  }];
  data.affiliatedGroups.forEach((g, i) => {
    blocks.push({
      id: g.id,
      entityNumber: i + 2,
      name: g.name,
      taxId: g.taxId,
      employees: g.employees,
      covered: g.covered,
      errorPrefix: `affiliatedGroups.${i}.`,
      isPrimary: false,
    });
  });
  return blocks;
}

/** Entity list for the Ownership Breakdown section: the primary always,
 *  plus every affiliated entity whose Covered Under Policy is Yes. Uncovered
 *  affiliates on the single-employer CC-Group path never collect ownership
 *  (ADO-3490) and are excluded from this section (ADO-3492 §3). Numbering
 *  matches Plan Structure so the two sections use identical headers. */
export function buildOwnershipBlocks(data: IntakeFormData): EntityBlock[] {
  const affiliatedActive = isAffiliatedListShown(data);
  const blocks: EntityBlock[] = [{
    id: PRIMARY_ENTITY_ID,
    entityNumber: 1,
    name: data.companyLegalName || "Primary group",
    taxId: data.federalTaxId,
    employees: data.primaryEligibleEmployees,
    covered: true,
    isPrimary: true,
  }];
  if (affiliatedActive) {
    data.affiliatedGroups.forEach((g, i) => {
      if (g.covered === true) {
        blocks.push({
          id: g.id,
          entityNumber: i + 2,
          name: g.name,
          taxId: g.taxId,
          employees: g.employees,
          covered: g.covered,
          errorPrefix: `affiliatedGroups.${i}.`,
          isPrimary: false,
        });
      }
    });
  }
  return blocks;
}

/** Shared entity-block heading text — `Entity 1 (Primary): Acme` vs
 *  `Entity 2: Subsidiary`. AC §§2, 3 use this exact wording in both sections. */
export function entityHeaderLabel(b: EntityBlock): string {
  const suffix = b.name ? `: ${b.name}` : "";
  const primaryTag = b.isPrimary ? " (Primary)" : "";
  return `Entity ${b.entityNumber}${primaryTag}${suffix}`;
}
